import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../ui/components/Input.jsx';
import Button from '../../ui/components/Button.jsx';
import { ErrorAlert } from '../../ui/components/Alert.jsx';
import { useTranslation } from '../../i18n';
import { useAuth } from '../hooks';
import AuthService from '../services/AuthService';
import { trackEvent } from '../../analytics/services/analyticsService';
import '../../ui/components/Form.css';

/**
 * LoginForm - Based on images/login/login.html mockup
 * UI-only for Task 2 (no API calls yet); emits onSubmit with { email, password }
 */
const LoginForm = function LoginForm({ onSubmit, onSuccess, disabled = false, className = '', testId }) {
  const { t } = useTranslation();
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const errors = useMemo(() => {
    const e = {};
    if (touched.email && (!email || !/.+@.+\..+/.test(email))) {
      e.email = t('validation.emailInvalid');
    }
    if (touched.password && (!password || password.length < 8)) {
      e.password = t('validation.passwordTooShort', { min: 8 });
    }
    return e;
  }, [email, password, touched, t]);

  const handleSubmit = useCallback(async (ev) => {
    ev.preventDefault();
    setTouched({ email: true, password: true });
    setSubmitError(null);
    if (Object.keys(errors).length > 0) return;

    // External submit handler (optional)
    if (onSubmit) onSubmit({ email, password });

    setSubmitting(true);
    
    const { data, error } = await AuthService.login({ email, password });
    console.log('🔍 LoginForm: API response:', { data, error });
    
    if (error) {
      console.log('❌ LoginForm: Error details:', error);
      setSubmitError(error.message || t('errors.generic'));
      setSubmitting(false);
      return;
    }

    if (data && data.accessToken) {
      await authLogin({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresIn: data.expiresIn || 1800,
        user: data.user || null
      });
      trackEvent('login', { method: 'email' });
      if (onSuccess) onSuccess(data);
    } else {
      setSubmitError(t('errors.generic'));
    }
    
    setSubmitting(false);
  }, [errors, onSubmit, email, password, authLogin, onSuccess, t]);

  return (
    <form className={'arbiwin-form arbiwin-form--card ' + className} onSubmit={handleSubmit} data-testid={testId || 'login-form'}>
      <div className="arbiwin-form__header">
        <h2 className="arbiwin-form__title">{t('auth.login.title')}</h2>
      </div>

      <div className="arbiwin-form__body">
        {submitError && (
          <div className="arbiwin-form__group">
            <ErrorAlert>
              {submitError}
            </ErrorAlert>
          </div>
        )}
        <div className="arbiwin-form__group">
          <Input
            type="email"
            label={t('auth.login.email')}
            placeholder={t('auth.login.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((s) => ({ ...s, email: true }))}
            required
            fullWidth
            error={errors.email}
          />
        </div>

        <div className="arbiwin-form__group">
          <Input
            type="password"
            label={t('auth.login.password')}
            placeholder={t('auth.login.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((s) => ({ ...s, password: true }))}
            required
            fullWidth
            showPassword
            error={errors.password}
          />
        </div>

        <div className="arbiwin-form__actions arbiwin-form__actions--end">
          <a
            className="arbiwin-link"
            href="/forgot-password"
            onClick={(event) => {
              event.preventDefault();
              navigate('/forgot-password');
            }}
          >
            {t('auth.login.forgotPassword')}
          </a>
        </div>
      </div>

      <div className="arbiwin-form__footer">
        <Button type="submit" variant="primary" size="large" disabled={disabled || submitting} fullWidth>
          {submitting ? t('feedback.loading') : t('auth.login.submit')}
        </Button>
        <p className="arbiwin-form__hint">
          {t('auth.login.noAccount')}{' '}
          <a
            className="arbiwin-link"
            href="/register"
            onClick={(event) => {
              event.preventDefault();
              navigate('/register');
            }}
          >
            {t('auth.register.title')}
          </a>
        </p>
      </div>
    </form>
  );
};

LoginForm.displayName = 'LoginForm';

export default LoginForm;


